import React, { useCallback } from 'react';

import { useIntl } from 'react-intl-next';
import { graphql, useMutation } from 'react-relay';
import type { RecordProxy, RecordSourceSelectorProxy } from 'relay-runtime';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useFlags } from '@atlaskit/flag';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { token } from '@atlaskit/tokens';
import { useHubContext } from '@atlassian/company-hub-common';

/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import { LogoUpload } from '../components/logo-upload';
import { messages } from '../messages';
/* eslint-enable @atlassian/tangerine/import/no-parent-imports */

/**
 * Helper function to get the logo record from the Relay store.
 * Returns null if any part of the path doesn't exist.
 */
function getLogoRecord(
	store: RecordSourceSelectorProxy,
	cloudId: string,
	helpCenterAri: string,
): RecordProxy | null {
	const root = store.getRoot();
	const helpCenterField = root.getLinkedRecord('helpCenter', { cloudId });
	if (!helpCenterField) {
		return null;
	}

	const helpCenterById = helpCenterField.getLinkedRecord('helpCenterById', { helpCenterAri });
	if (!helpCenterById) {
		return null;
	}

	const brandingRecord = helpCenterById.getLinkedRecord('helpCenterBranding');
	if (!brandingRecord) {
		return null;
	}

	return brandingRecord.getLinkedRecord('logo');
}

export interface LogoUploadSectionProps {
	onLogoChange: () => void;
}

/**
 * LogoUploadSection component handles logo upload and delete operations.
 * It wraps the LogoUpload UI component and manages the GraphQL mutations,
 * Relay cache updates, analytics, and user feedback.
 */
export function LogoUploadSection({ onLogoChange }: LogoUploadSectionProps): React.JSX.Element | null {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlags();

	const { cloudId, branding, hubMediaConfig, env } = useHubContext();

	// GraphQL mutation to update hub branding
	const [commitUpdateHubBranding] = useMutation(graphql`
		mutation logoUploadSectionUpdateBrandingMutation(
			$cloudId: ID!
			$input: HelpCenterUpdateInput!
		) {
			helpCenter(cloudId: $cloudId) @optIn(to: "HelpCenterAggBeta") {
				updateHelpCenter(input: $input) {
					success
					errors {
						message
					}
				}
			}
		}
	`);

	// Handle logo upload with backend API call
	const handleLogoUpload = useCallback(
		(fileId: string) => {
			// Construct the public logo URL with client and token query params
			const newLogoUrl = hubMediaConfig
				? `${hubMediaConfig.mediaUrl}/file/${fileId}/image?client=${hubMediaConfig.asapIssuer}&token=${hubMediaConfig.mediaToken}&collection=${hubMediaConfig.mediaCollectionName}`
				: '';

			commitUpdateHubBranding({
				variables: {
					cloudId,
					input: {
						helpCenterAri: env.helpCenterAri,
						helpCenterBranding: {
							logo: {
								fileId: fileId,
							},
						},
					},
				},
				updater: (store) => {
					// Update the Relay store so the Hub context reflects the new logo immediately
					const logoRecord = getLogoRecord(store, cloudId, env.helpCenterAri);
					if (logoRecord) {
						logoRecord.setValue(newLogoUrl, 'url');
						logoRecord.setValue(fileId, 'fileId');
					}
				},
				onCompleted: (response: any) => {
					if (response?.helpCenter?.updateHelpCenter?.success) {
						// Notify parent component about the change
						onLogoChange();

						// Track success analytics
						createAnalyticsEvent({
							type: 'sendUIEvent',
							data: {
								action: 'uploaded',
								actionSubject: 'logo',
								actionSubjectId: 'hubLogo',
								source: 'hubThemingConfig',
								attributes: {
									fileId: fileId,
								},
							},
						}).fire();
					} else {
						// Show error flag
						showFlag({
							title: formatMessage(messages.logoUploadError),
							isAutoDismiss: true,
							icon: (
								<StatusErrorIcon
									label={formatMessage(messages.logoUploadError)}
									color={token('color.icon.danger')}
									spacing="spacious"
								/>
							),
						});

						// Track failure analytics
						createAnalyticsEvent({
							type: 'sendUIEvent',
							data: {
								action: 'failed',
								actionSubject: 'logo',
								actionSubjectId: 'hubLogoUpload',
								source: 'hubThemingConfig',
								attributes: {
									fileId: fileId,
									errors: response?.helpCenter?.updateHelpCenter?.errors,
								},
							},
						}).fire();
					}
				},
				onError: (error: Error) => {
					showFlag({
						title: formatMessage(messages.logoUploadError),
						description: error.message,
						isAutoDismiss: true,
						icon: (
							<StatusErrorIcon
								label={formatMessage(messages.logoUploadError)}
								color={token('color.icon.danger')}
								spacing="spacious"
							/>
						),
					});

					// Track error analytics
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'failed',
							actionSubject: 'logo',
							actionSubjectId: 'hubLogoUpload',
							source: 'hubThemingConfig',
							attributes: {
								fileId: fileId,
								error: error.message,
							},
						},
					}).fire();
				},
			});
		},
		[
			commitUpdateHubBranding,
			cloudId,
			env.helpCenterAri,
			formatMessage,
			showFlag,
			hubMediaConfig,
			createAnalyticsEvent,
			onLogoChange,
		],
	);

	// Handle logo delete with backend API call
	const handleLogoDelete = useCallback(() => {
		// Call the same GraphQL mutation with empty string fileId to delete the logo
		commitUpdateHubBranding({
			variables: {
				cloudId,
				input: {
					helpCenterAri: env.helpCenterAri,
					helpCenterBranding: {
						logo: {
							fileId: '',
						},
					},
				},
			},
			updater: (store) => {
				// Update the Relay store to clear the logo from Hub context
				const logoRecord = getLogoRecord(store, cloudId, env.helpCenterAri);
				if (logoRecord) {
					logoRecord.setValue('', 'url');
					logoRecord.setValue('', 'fileId');
				}
			},
			onCompleted: (response: any) => {
				const updateResult = response?.helpCenter?.updateHelpCenter;
				if (updateResult?.success) {
					// Notify parent component about the change
					onLogoChange();

					// Track success analytics
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'deleted',
							actionSubject: 'logo',
							actionSubjectId: 'hubLogo',
							source: 'hubThemingConfig',
						},
					}).fire();
				} else {
					showFlag({
						title: formatMessage(messages.logoDeletionError),
						isAutoDismiss: true,
						icon: (
							<StatusErrorIcon
								label={formatMessage(messages.logoDeletionError)}
								color={token('color.icon.danger')}
								spacing="spacious"
							/>
						),
					});

					// Track failure analytics
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'failed',
							actionSubject: 'logo',
							actionSubjectId: 'hubLogoDelete',
							source: 'hubThemingConfig',
							attributes: {
								errors: updateResult?.errors,
							},
						},
					}).fire();
				}
			},
			onError: (error: Error) => {
				showFlag({
					title: formatMessage(messages.logoDeletionError),
					description: error.message,
					isAutoDismiss: true,
					icon: (
						<StatusErrorIcon
							label={formatMessage(messages.logoDeletionError)}
							color={token('color.icon.danger')}
							spacing="spacious"
						/>
					),
				});

				// Track error analytics
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'failed',
						actionSubject: 'logo',
						actionSubjectId: 'hubLogoDelete',
						source: 'hubThemingConfig',
						attributes: {
							error: error.message,
						},
					},
				}).fire();
			},
		});
	}, [
		commitUpdateHubBranding,
		cloudId,
		env.helpCenterAri,
		formatMessage,
		showFlag,
		createAnalyticsEvent,
		onLogoChange,
	]);

	// Check if media config is available
	if (!hubMediaConfig) {
		// Track when media config is missing for observability
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'failed',
				actionSubject: 'logoSection',
				actionSubjectId: 'hubLogoMediaConfigMissing',
				source: 'hubThemingConfig',
				attributes: {
					reason: 'hubMediaConfig is not available',
				},
			},
		}).fire();

		return null;
	}

	return (
		<LogoUpload
			contentId={env.helpCenterId}
			logoUrl={branding.logo?.url}
			onLogoUpload={handleLogoUpload}
			onLogoDelete={handleLogoDelete}
			customMediaConfig={{
				mediaToken: hubMediaConfig.mediaToken,
				mediaUrl: hubMediaConfig.mediaUrl,
				collection: hubMediaConfig.mediaCollectionName,
				asapIssuer: hubMediaConfig.asapIssuer,
			}}
		/>
	);
}
